import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Inject, Injectable } from '@angular/core';
import { Observable, catchError, map, tap, throwError } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { CustomerLoginInput, VarifyEmailCode, EmailInput, resetPassword, GtokenInput, FtokenInput } from '../../inputs';
import { HttpService } from './http.service';
import { LocalStorageService } from '../common/local-storage.service';
import { ActiveUserService } from '../common/active-user.service';
import { CustomerLoginResponse, CustomerLoginResponseDetail } from '../../models/common/app-token.model';
import { ToastService } from '../common/toast.service';
import { LanguageService } from '../common/language.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService extends HttpService<CustomerLoginInput | EmailInput | VarifyEmailCode | resetPassword | GtokenInput | FtokenInput | '' | { newPassword: string; }, CustomerLoginResponse | CustomerLoginResponseDetail> {
  constructor(
    httpClient: HttpClient,
    @Inject('API_URL') apiUrl: string,
    @Inject('API_VERSION') apiVersion: string,
    private routrer: Router,
    private jwtService: JwtHelperService,
    private activeUserService: ActiveUserService,
    private localStorageService: LocalStorageService,
  ) {
    super(httpClient, apiUrl, apiVersion, 'auth');

    this.isAuthenticated();
    
    // Initialize guest user in background if no token exists
    // This runs after app initialization to prevent blocking initial render
    if (typeof window !== 'undefined') {
      setTimeout(() => {
        const hasAccessToken = this.localStorageService.getRToken();
        const hasGuestAccessToken = this.localStorageService.getRGToken();
        
        if (!hasAccessToken && !hasGuestAccessToken) {
          this.guestAuthenticate().subscribe({
            error: (err) => console.warn('Failed to initialize guest user:', err)
          });
        }
      }, 0);
    }
  }
  private messageService = inject(ToastService);

  isAuthenticated() {
    const token = this.localStorageService.getRToken();
    if (!token || token.split('.').length !== 3) {
      return false;
    }
    let userState = this.localStorageService.getUserState();
    if (userState == "true") {
      this.messageService.error('Session expired, please login again');
      // alert('Session expired, please login again');
      this.logOut();
      return false;
    }
    
    return !!token;
  }
languageService = inject(LanguageService);
  authenticate(login: CustomerLoginInput): Observable<number> {
    // Call the create method, which now returns HttpResponse<CustomerLoginResponse>
    return this.createHttpRes(login, 'login').pipe(
      tap((res: HttpResponse<CustomerLoginResponseDetail>) => { // 'res' is now a HttpResponse object
        // Check the status code
        if (res.status === 200) {
          if (res.body) {
            this.localStorageService.setToken({ accessToken: res.body.data.accessToken, refreshToken: res.body.data.refreshToken });
            if(!this.languageService.getCurrentLanguage()){
              this.localStorageService.setLanguage(res.body.data.language);
            }
            this.localStorageService.setGustUser("false");
            this.activeUserService.setActiveUser();
            this.localStorageService.setUserInfo(this.activeUserService.getActiveUser());
          }
        }
      }),
      map((res: HttpResponse<CustomerLoginResponseDetail>) => res.status)
    );
  }
  
  guestAuthenticate(): Observable<void> {
    return this.create('', 'guest').pipe(
      map((res) => {
        if (res) {
          this.localStorageService.setGuestToken(res.data);
          this.localStorageService.setGustUser("true");
          this.activeUserService.updateGuestStatus();
          this.activeUserService.setActiveUser();
          // Removed window.location.reload() - prevents FOUC on first visit
          // State management handles updates reactively
        }
      })
    );
  }
  varfyEmail(email: EmailInput): Observable<void> {
    return this.create(email, 'verify-email').pipe(
      map((res) => {
        if (res) {
          // this.localStorageService.setCount(res.data);
        }
      })
    );
  }
  varfyEmailCode(email: VarifyEmailCode): Observable<void> {
    return this.create(email, 'verify-email/code').pipe(
      map((res) => {
        if (res) {
          this.localStorageService.setToken({ accessToken: res.data.accessToken, refreshToken: res.data.refreshToken });
          this.localStorageService.setGustUser("false");
          this.activeUserService.setActiveUser();
          this.localStorageService.setUserInfo(this.activeUserService.getActiveUser());
        }
      })
    );
  }
  varfyForgotEmailCode(email: VarifyEmailCode): Observable<{ accessToken: string }> {
    return this.create(email, 'forgot-password/code').pipe(
      map((res) => {
        return res?.data; // Ensure this returns `{ token: string }`
      })
    );
  }
  resetPassword(password: resetPassword): Observable<void> {
    return this.createHeaderToken({ newPassword: password.newPassword }, 'forgot-password/update', password.token).pipe(
      map((res) => {
      })
    );
  }
  loginWithGoogleIdToken(token: GtokenInput): Observable<void> {
    return this.create(token, 'google/token').pipe(
      map((res) => {
        this.localStorageService.setToken(res.data);
        this.localStorageService.setGustUser("false");
        this.activeUserService.setActiveUser();
        this.localStorageService.setUserInfo(this.activeUserService.getActiveUser());
      })
    );
  }
  loginWithFBIdToken(token: FtokenInput): Observable<void> {
    return this.create(token, 'facebook/token').pipe(
      map((res) => {
        this.localStorageService.setToken(res.data);
        this.localStorageService.setGustUser("false");
        this.activeUserService.setActiveUser();
        this.localStorageService.setUserInfo(this.activeUserService.getActiveUser());

      })
    );
  }

  authenticateViaGoogle(): void {
    window.location.href = `${this.apiUrl}/google`;
  }

  authGoogleUser(token: string): void {
    this.localStorageService.setToken(token);
    this.activeUserService.setActiveUser();
  }

  register(userInfo: CustomerLoginInput) {
    return this.create(userInfo, 'register');
  }

  forgot(email: EmailInput) {
    return this.create(email, 'forgot-password');
  }

  logOut(): void {
    this.localStorageService.clearTokens();
    this.activeUserService.clearActiveUser();
    this.routrer.navigate(['auth']);
  }

  accountDeleted():void{
    this.localStorageService.clearTokens();
    this.activeUserService.clearActiveUser();
    this.routrer.navigate(['/']);
  }

  deleteAccount(user:any){
    return this.create(user, 'account/delete');
  }



}
